/**
 * @module Service
 */

import { environment } from 'environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Company, CompanyMemberRef } from '@feature/companies/shared';
import { Page, PageRequest } from '@app/models/page.model';
import { AddUserResponse } from '@feature/users/shared';
import { BaseHttpService } from '@app/services/base-http.service';
import { Document } from '@feature/document-center/shared/document.model';

/**
 * Service for managing company-related operations within the application.
 *
 * This service extends the BaseHttpService to provide additional functionality
 * specific to company management, such as fetching, creating or updating company data,
 * and handling member relationships. It uses Angular HttpClient for making HTTP requests
 * to the backend API. It is provided at the root level, making it available
 * throughout the application.
 *
 * @service
 * @extends {BaseHttpService}
 * @@Injectable
 */
@Injectable({ providedIn: 'root' })
export class CompanyService extends BaseHttpService {
	/**
	 * Options for HTTP requests, including headers.
	 * @type {{ headers: HttpHeaders }}
	 * @note Used for the CRUD operations on the user.
	 */
	options: { headers: HttpHeaders } = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

	/**
	 * Creates an instance of the CompanyService.
	 *
	 * @constructor
	 * @param {HttpClient} _http - The HttpClient used for making HTTP requests.
	 */
	constructor(private _http: HttpClient) {
		super(_http);
	}

	/**
	 * Retrieves a company by its unique identifier.
	 *
	 * This method sends a GET request to fetch the details of a specific company based on the provided
	 * ID. The ID is URL-encoded to ensure it is safe for inclusion in the request URL.
	 *
	 * @param {any} id - The unique identifier of the company to retrieve.
	 * @returns {Observable<Company>} An observable containing the requested company object.
	 */
	public getById(id: any): Observable<Company> {
		id = encodeURIComponent(id);
		return this._http.get<Company>(`${environment.config.apiUrl}/companies/${id}`);
	}

	/**
	 * Retrieves a paginated list of companies, optionally filtered by search term and type.
	 *
	 * This method constructs the necessary HTTP parameters for pagination and search, then sends
	 * a GET request to retrieve the companies from the API. The response is transformed to ensure
	 * that each company is an instance of the Company class.
	 *
	 * @param {string} [search] - Optional search term to filter the companies.
	 * @param {string} [type] - Optional type to filter the companies.
	 * @param {PageRequest} [pageRequest] - Optional pagination parameters.
	 * @returns {Observable<Page<Company>>} An observable containing the paginated list of companies.
	 */
	public getCompanies(search?: string, type? : string, pageRequest?: PageRequest): Observable<Page<Company>> {
		let params: HttpParams = this.params(pageRequest, search);
		if (type) {
			params = params.append('type', type);
		}

		const result: Observable<Page<Company>> = this._http.get<Page<Company>>(`${environment.config.apiUrl}/companies`, { params });
		return result.pipe(map((page: Page<Company>) => {
			page.content = page.content.map((company: Company) => Object.assign(new Company(), company));
			return page;
		}));
	}

	/**
	 * Retrieves a reference to a company member by their company ID and member ID.
	 *
	 * This method sends a GET request to fetch the details of a specific company member associated
	 * with the given company ID and member ID.
	 *
	 * @param {number} companyId - The unique identifier of the company.
	 * @param {number} memberId - The unique identifier of the member.
	 * @returns {Observable<CompanyMemberRef>} An observable containing the company member reference.
	 */
	public getRefByCompanyIdAndMemberId(companyId: number, memberId: number): Observable<CompanyMemberRef> {
		return this._http.get<CompanyMemberRef>(`${environment.config.apiUrl}/companies/${companyId}/members/${memberId}`);
	}

	/**
	 * Retrieves a list of companies associated with a specific member.
	 *
	 * This method sends a GET request to fetch the companies linked to the specified member ID.
	 *
	 * @param {number} memberId - The unique identifier of the member.
	 * @returns {Observable<CompanyMemberRef[]>} An observable containing an array of company member references.
	 */
	public getCompaniesByMemberId(memberId: number): Observable<CompanyMemberRef[]> {
		return this._http.get<CompanyMemberRef[]>(`${environment.config.apiUrl}/users/${memberId}/companies`);
	}

	/**
	 * Retrieves a paginated list of members associated with a specific company.
	 *
	 * This method sends a GET request to fetch the members linked to the specified company ID,
	 * with optional pagination and search parameters.
	 *
	 * @param {number} companyId - The unique identifier of the company.
	 * @param {PageRequest} [pageable] - Optional pagination parameters.
	 * @param {string} [search] - Optional search term to filter members.
	 * @returns {Observable<Page<CompanyMemberRef>>} An observable containing the paginated list of company members.
	 */
	public getCompanyMembersByCompanyId(companyId: number, pageable?: PageRequest, search?: string): Observable<Page<CompanyMemberRef>> {
		return this._http.get<Page<CompanyMemberRef>>(`${environment.config.apiUrl}/companies/${companyId}/members`, { params: this.params(pageable, search) });
	}

	/**
	 * Get payer company documents per required document category.
	 *
	 * @param {number} userId
	 * @param {number} categoryId
	 * @returns {Observable<Document[]>}
	 */
	public getCompanyDocumentsPerRequiredDocumentCategory(companyId: number, categoryId: number): Observable<Array<Document>> {
		let performerParams = new HttpParams();
		performerParams = performerParams.append('categoryId', (categoryId != null ? categoryId.toString() : ''));
		return this._http.get<Array<Document>>(`${environment.config.apiUrl}/companies/${companyId}/required-documents`, { params: performerParams });
	}

	/**
	 * Creates a new company based on the provided company object.
	 *
	 * This method sends a POST request to create a new company using the specified company data.
	 *
	 * @param {Company} company - The company object containing the details of the company to create.
	 * @returns {Observable<Company>} An observable containing the created company object.
	 */
	public createCompany(company: Company): Observable<Company> {
		return this._http.post<Company>(`${environment.config.apiUrl}/companies`, company, this.options);
	}

	/**
	 * Updates a company profile reference, optionally with a step identifier.
	 *
	 * @param {Company} company - A company containing the updated information.
	 * @param {string} [step] - An optional step identifier for tracking the update process.
	 * @returns {Observable<Company>}
	 */
	public updateCompany(company: Company, step?: string): Observable<Company> {
		let profileParams: HttpParams = new HttpParams();
		profileParams = profileParams.append('step', (step != null ? step : null));
		return this._http.put<Company>(`${environment.config.apiUrl}/companies/${company.id}`, company, { params: profileParams });
	}

	/**
	 * Checks if a member can be added to a specified company based on their email.
	 *
	 * This method sends a GET request to determine if the user with the provided email can be added
	 * as a member of the specified company. The email is URL-encoded to ensure it is safe for inclusion
	 * in the request URL.
	 *
	 * @param {number} companyId - The unique identifier of the company.
	 * @param {string} memberEmail - The email address of the member to check.
	 * @returns {Observable<AddUserResponse>} An observable containing the response indicating if the member can be added.
	 */
	public canAddMember(companyId: number, memberEmail: string): Observable<AddUserResponse> {
		memberEmail = encodeURIComponent(memberEmail);
		return this._http.get<AddUserResponse>(`${environment.config.apiUrl}/companies/${companyId}/can-add-member/${memberEmail}`);
	}

	/**
	 * Creates a new member reference for a specified company.
	 *
	 * This method sends a POST request to create a new member reference associated with the given
	 * company. If the createMember flag is set to true, it appends a query parameter to the request URL.
	 *
	 * @param {CompanyMemberRef} companyMemberRef - The company member reference object to create.
	 * @param {boolean} [createMember=false] - Optional flag indicating whether to create a new member.
	 * @returns {Observable<CompanyMemberRef>} An observable containing the created company member reference.
	 */
	public createMemberRef(companyMemberRef: CompanyMemberRef, createMember?: boolean): Observable<CompanyMemberRef> {
		let requestUrl = `${environment.config.apiUrl}/companies/${companyMemberRef.companyId}/members`;
		if (createMember) {
			requestUrl += '?createMember=true';
		}
		return this._http.post<CompanyMemberRef>(requestUrl, companyMemberRef, this.options);
	}

	/**
	 * Deletes a member from a specified company.
	 *
	 * This method sends a DELETE request to remove the specified member from the given company.
	 *
	 * @param {number} companyId - The unique identifier of the company.
	 * @param {number} memberId - The unique identifier of the member to delete.
	 * @returns {Observable<CompanyMemberRef>} An observable containing the deleted company member reference.
	 */
	public deleteMember(companyId: number, memberId: number): Observable<CompanyMemberRef> {
		return this._http.delete<CompanyMemberRef>(`${environment.config.apiUrl}/companies/${companyId}/members/${memberId}`);
	}

	/**
	 * Resends a membership request for a specified member in a company.
	 *
	 * This method sends a PUT request to resend the membership request for the specified member
	 * within the given company.
	 *
	 * @param {number} companyId - The unique identifier of the company.
	 * @param {number} memberId - The unique identifier of the member whose request is being resent.
	 * @returns {Observable<CompanyMemberRef>} An observable containing the updated company member reference.
	 */
	public resendMembershipRequest(companyId: number, memberId: number): Observable<CompanyMemberRef> {
		return this._http.put<CompanyMemberRef>(`${environment.config.apiUrl}/companies/${companyId}/members/${memberId}`, null, this.options);
	}

	/**
	 * Accepts a membership request based on the provided request key.
	 *
	 * This method sends a PUT request to accept the membership request identified by the specified key.
	 *
	 * @param {string} key - The unique key identifying the membership request to accept.
	 * @returns {Observable<CompanyMemberRef>} An observable containing the accepted company member reference.
	 */
	public acceptMember(key: string): Observable<CompanyMemberRef> {
		return this._http.put<CompanyMemberRef>(`${environment.config.apiUrl}/company-member-request/${key}/accept`, null);
	}

	/**
	 * Declines a membership request based on the provided key.
	 *
	 * This method sends a PUT request to decline the membership request identified by the specified key.
	 *
	 * @param {string} key - The unique key identifying the membership request to decline.
	 * @returns {Observable<CompanyMemberRef>} An observable containing the declined company member reference.
	 */
	public declineMember(key: string): Observable<CompanyMemberRef> {
		return this._http.put<CompanyMemberRef>(`${environment.config.apiUrl}/company-member-request/${key}/decline`, null);
	}

	/**
	 * Updates the reference of a member based on the provided user agent reference.
	 *
	 * This method sends a PUT request to update the details of the specified company member reference.
	 *
	 * @param {CompanyMemberRef} ref - The company member reference to update.
	 * @returns {Observable<CompanyMemberRef>} An observable containing the updated company member reference.
	 */
	public updateMemberRef(ref: CompanyMemberRef): Observable<CompanyMemberRef> {
		return this._http.put<CompanyMemberRef>(`${environment.config.apiUrl}/company-member-request/${ref.id}`, ref);
	}
}
